/**
 * The getting-started card's whole decision, as one pure function
 * (`docs/design-onboarding-flows.md` §4.1, `rl-757.46`).
 *
 * It lives outside the component because §4.1 is a *table* — four account
 * states × three steps, each with its own copy, its own done-ness and one
 * footer that only one state carries. A table is worth testing row by row,
 * and mounting a card to find out which step is current turns a data
 * question into a DOM question.
 *
 * Pure and session-free: every input arrives as an argument, nothing here
 * reads a store, the router or the clock (`now` is injectable so §5's
 * "add the year only when the month is more than a year away" rule is
 * testable rather than calendar-dependent).
 */

/**
 * Which of §4.1's four rows an account is on, resolved in priority order by
 * {@link accountStateFor}. The order matters more than the values do: an
 * account can satisfy two of these at once.
 */
export type AccountState =
  /** Holds a seat someone else funds — claimed or funded. Step 2 is already done. */
  | "funded_holder"
  /** Pays for at least one seat and holds no entitling seat of their own. */
  | "payer"
  /** Under §12.1's purchase threshold: no plan step, and no purchase control anywhere. */
  | "learner_under_16"
  /** Everyone else, including "we do not know yet" — see {@link accountStateFor}. */
  | "learner_16_plus";

export type StepStatus = "done" | "current" | "upcoming";

/** The step's right-slot control, or `null` where §4.1 offers none. */
export type StepAction =
  /** Both verify actions, rendered inline in the step (§4.1, last paragraph). */
  | "verify"
  /** Opens the plan surface. */
  | "plan"
  /** Opens the add-subject slide-over. */
  | "subject";

export type StepId = "verify" | "plan" | "ask" | "seat" | "subject" | "claim";

export interface GettingStartedStep {
  id: StepId;
  title: string;
  /** `null` where the row is a bare title — §4.1 gives no description for it. */
  description: string | null;
  status: StepStatus;
  action: StepAction | null;
}

/**
 * The card's inputs. Mirrors `GettingStartedCard`'s props one-for-one, plus
 * the signed-in address: §4.1's unverified step names the mailbox the link
 * went to, and that is a session fact the card reads from the auth store
 * rather than a parent prop (`stores/auth.ts` `user`).
 *
 * Every field is required, for the reason the props are (`rl-757.45`): an
 * optional `emailVerified` reads as "unverified" wherever it is forgotten,
 * and an optional `canSelfPurchase` collapses to "under 16" — the one value
 * with child-safety copy attached.
 */
export interface GettingStartedInput {
  emailVerified: boolean;
  /** `null` = not known yet, and deliberately permissive — see {@link accountStateFor}. */
  canSelfPurchase: boolean | null;
  hasEntitlingSeat: boolean;
  /** Non-null => this account holds a seat someone else funds. */
  funderFirstName: string | null;
  /** ISO date; `current_period_end`, never the checkout sentinel (§7). */
  renewsAt: string | null;
  subjectCount: number;
  firstSubjectId: string | null;
  paidSeatCount: number;
  claimedSeatCount: number;
  email: string | null;
}

export interface GettingStartedView {
  accountState: AccountState;
  /** Card title while incomplete, strip title once complete. */
  title: string;
  /** Card sub-line while incomplete, strip body once complete. */
  subline: string;
  /** §4.1 gives a footer to the 16+ learner card only. */
  footer: string | null;
  /** Mono count, "1 OF 3 DONE" / "ALL DONE". */
  countLabel: string;
  doneCount: number;
  totalCount: number;
  complete: boolean;
  steps: GettingStartedStep[];
  /**
   * Whether the completion strip can offer its CTA. False for a payer (§4.1
   * gives their strip no action) and false when no subject id resolves, since
   * `/subjects/{id}?session=1` has nowhere to point without one.
   */
  showSessionCta: boolean;
}

/** §5's one label per action, so no surface can phrase one of these its own way. */
export const ACTION_LABELS = {
  plan: "Start your plan",
  subject: "Add your first subject",
  verified: "I have verified — continue",
  resend: "Send the link again",
  session: "Start your first session",
} as const;

/**
 * The checkout fallback writes a far-future `current_period_end` until the
 * subscription event carries the real one, so a date years out is a value
 * still settling and not a plan running until 2099 (§7).
 */
const RENEWAL_SENTINEL_YEAR = 2099;

const YEAR_MS = 365 * 24 * 60 * 60 * 1000;

/**
 * §5's date format: `d MMMM`, `en-GB` explicitly rather than the runtime
 * default — the same build otherwise prints "12 October" here and
 * "October 12" on a US-locale runner. Returns null when there is nothing
 * honest to print, and the caller drops the whole clause.
 */
export function renewalLabel(
  iso: string | null,
  now: Date = new Date(),
): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  if (date.getUTCFullYear() >= RENEWAL_SENTINEL_YEAR) return null;
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    // The year earns its place only when the month is ambiguous at that
    // distance; on a monthly plan it never is.
    ...(date.getTime() - now.getTime() > YEAR_MS ? { year: "numeric" } : {}),
  });
}

/**
 * §4.4 and D8's payer test: funds at least one seat and holds no entitling
 * seat of their own.
 *
 * Named and exported because two surfaces turn on it — the card's account
 * state below, and the sidebar's "Add subject" item (D8, rl-757.50) — and the
 * answer needs `GET /me/seats` and `GET /me/subscription` together. A second
 * spelling of a two-source fact drifts as soon as one source changes shape.
 *
 * Not "has the payer role": an account can fund a seat AND hold one, and that
 * person is a learner who happens to pay, so their nav and their Today stay
 * the ordinary ones.
 */
export function isPayerWithoutSeat(input: {
  paidSeatCount: number;
  hasEntitlingSeat: boolean;
}): boolean {
  return input.paidSeatCount > 0 && !input.hasEntitlingSeat;
}

/**
 * §4.1's four rows in priority order, because an account can sit on two of
 * them: a payer who was funded a seat is a holder first (their own step 2 is
 * settled), and a 13–15 account that someone funded is likewise a holder
 * rather than a learner still being told it cannot buy.
 *
 * `canSelfPurchase: null` — the age answer is not known yet — lands on 16+
 * deliberately. The permissive branch shows a plan CTA the
 * API refuses with `age_below_purchase_threshold` if it was wrong; the strict
 * branch would tell a paying adult they are a child. §12.1's enforcement is
 * the refusal, not this discriminator.
 */
export function accountStateFor(input: GettingStartedInput): AccountState {
  if (input.funderFirstName !== null) return "funded_holder";
  if (isPayerWithoutSeat(input)) return "payer";
  if (input.canSelfPurchase === false) return "learner_under_16";
  return "learner_16_plus";
}

interface StepSpec {
  id: StepId;
  title: string;
  description: string | null;
  done: boolean;
  action: StepAction | null;
}

function verifyStep(input: GettingStartedInput): StepSpec {
  return {
    id: "verify",
    title: "Confirm your email",
    // Only while it is outstanding: "we sent a link" reads as an instruction
    // on a row whose right slot already says DONE.
    description: input.emailVerified
      ? null
      : `We sent a link to ${input.email ?? "the address you registered with"}. Open it, then come back here.`,
    done: input.emailVerified,
    action: input.emailVerified ? null : "verify",
  };
}

function subjectStep(input: GettingStartedInput): StepSpec {
  return {
    id: "subject",
    title: ACTION_LABELS.subject,
    description: null,
    done: input.subjectCount > 0,
    action: "subject",
  };
}

function clause(parts: readonly (string | null)[]): string {
  return `${parts.filter((p) => p !== null).join(" · ")}.`;
}

function stepsFor(
  state: AccountState,
  input: GettingStartedInput,
  now: Date,
): StepSpec[] {
  const renews = renewalLabel(input.renewsAt, now);

  switch (state) {
    case "funded_holder":
      return [
        verifyStep(input),
        {
          id: "seat",
          title: "Your seat",
          description: clause([
            `Paid for by ${input.funderFirstName}`,
            "full access",
            renews && `renews ${renews}`,
          ]),
          // Someone else settled this step by paying; there was never an
          // action here for the holder to take.
          done: true,
          action: null,
        },
        subjectStep(input),
      ];

    case "payer":
      return [
        verifyStep(input),
        {
          id: "plan",
          title: ACTION_LABELS.plan,
          description: clause([
            "A seat for someone else",
            "£39 a month",
            renews && `renews ${renews}`,
          ]),
          done: true,
          action: null,
        },
        {
          id: "claim",
          title: "Hand over the claim link",
          // No control: the link itself is the copy field on the seats card
          // below (§4.4), and a second button that only scrolls to it would
          // be a control for nothing.
          description:
            "Send it however you already talk to them — a message is fine. " +
            "Whoever opens it claims the seat. The link is on the seat card below.",
          // Done when every seat paid for has left `pending_claim`: while one
          // has not, there is still a link in the payer's hands.
          done: input.claimedSeatCount >= input.paidSeatCount,
          action: null,
        },
      ];

    case "learner_under_16":
      return [
        verifyStep(input),
        {
          id: "ask",
          title: "Ask someone to pay for your seat",
          // Description only, no button and no link: §12.1 puts a
          // self-purchase control out of reach here, so this row explains the
          // route instead of offering one.
          description:
            "Plans are bought by someone 16 or over — usually a parent or carer. " +
            "We send them a request and nothing is charged until they choose to pay.",
          done: input.hasEntitlingSeat,
          action: null,
        },
        subjectStep(input),
      ];

    case "learner_16_plus":
      return [
        verifyStep(input),
        {
          id: "plan",
          title: ACTION_LABELS.plan,
          description:
            "£39 a month for every subject and board. Cancel any time.",
          done: input.hasEntitlingSeat,
          action: "plan",
        },
        subjectStep(input),
      ];
  }
}

/** §4.1's footer, which only the 16+ learner card carries. */
const LEARNER_FOOTER =
  "Would someone else be paying? Ask someone to pay — nothing is charged " +
  "until they choose to.";

const SUBLINE = "Do them in any order — this is the quickest route.";
const PAYER_SUBLINE =
  "You have paid for one seat. Here is how it reaches them.";

/**
 * The completion strip's copy (§4.1). The payer body stops at "Nothing else
 * for you to do": §4.1 continues "We will email you if a payment ever fails",
 * but the outbox dead-letters every `email` row, so no payer dunning mail
 * sends today (`rl-njh.6`, `rl-757.29`) and the sentence would be a promise
 * we cannot keep. It lands with that mail, not before.
 */
const STRIP = {
  learner: {
    title: "You're set up",
    body:
      "Your first session builds a picture of what you already know. " +
      "20 minutes is plenty.",
  },
  payer: {
    title: "All done — the seat has been claimed",
    body: "Nothing else for you to do.",
  },
} as const;

export function deriveGettingStarted(
  input: GettingStartedInput,
  now: Date = new Date(),
): GettingStartedView {
  const accountState = accountStateFor(input);
  const payer = accountState === "payer";
  const specs = stepsFor(accountState, input, now);

  // Exactly one `current`: the first step that is not done. Everything after
  // it is `upcoming` even when it happens to be done — §4.1's dot states are
  // a position in the route, not a per-row truth.
  const firstOpen = specs.findIndex((s) => !s.done);
  const steps: GettingStartedStep[] = specs.map((spec, i) => ({
    id: spec.id,
    title: spec.title,
    description: spec.description,
    status: spec.done ? "done" : i === firstOpen ? "current" : "upcoming",
    action: spec.done || i !== firstOpen ? null : spec.action,
  }));

  const totalCount = steps.length;
  const doneCount = steps.filter((s) => s.status === "done").length;
  const complete = doneCount === totalCount;
  const remaining = totalCount - doneCount;
  const strip = payer ? STRIP.payer : STRIP.learner;

  return {
    accountState,
    title: complete
      ? strip.title
      : payer
        ? "Handing over a seat"
        : `${remaining} step${remaining === 1 ? "" : "s"} and you're studying`,
    subline: complete ? strip.body : payer ? PAYER_SUBLINE : SUBLINE,
    footer:
      complete || accountState !== "learner_16_plus" ? null : LEARNER_FOOTER,
    countLabel: complete ? "ALL DONE" : `${doneCount} OF ${totalCount} DONE`,
    doneCount,
    totalCount,
    complete,
    steps,
    showSessionCta: complete && !payer && input.firstSubjectId !== null,
  };
}
